// src/views/PickingReplenishment.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaSearch, FaTimes } from 'react-icons/fa';
import Split from 'react-split';
import './PickingReplenishment.css';
import { showToast, hideToast } from '../utils/toast';

const API = import.meta.env.VITE_API_URL;
const WH_PICK_LS   = 'wd_selected_warehouse_v1';
const AREA_PICK_LS = 'wd_selected_area_v2';
const PR_CLIENT_LS = 'pr_selected_client_v1';
const PR_SKUQ_LS   = 'pr_sku_query_v1';
const TOAST_IDS = {
  aisles: 'pr-aisles',
  bays: 'pr-bays',
  shelves: 'pr-shelves',
  slots: 'pr-slots',
  locations: 'pr-pick-locations'
};

async function fetchJSON(url, opts) {
  const res = await fetch(url, opts);
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try { msg = (await res.json())?.error || msg; } catch {}
    throw new Error(msg);
  }
  return res.json();
}

function pad3(n) { return n == null || n === '' ? '' : String(n).padStart(3, '0'); }
function pad2(n) { return n == null || n === '' ? '' : String(n).padStart(2, '0'); }

export default function PickingReplenishment() {
  // ---- Left: SKU search ----
  const [skuQuery, setSkuQuery] = useState(() => {
    try { return String(localStorage.getItem(PR_SKUQ_LS) || ''); } catch { return ''; }
  });
  const [invRows, setInvRows] = useState([]); // left table
  const [invLoading, setInvLoading] = useState(false);
  const debounceRef = useRef(null);
  // optional client filter (internal users only)
  const [clientOptions, setClientOptions] = useState([]);
  const [clientId, setClientId] = useState(() => {
    try { return String(localStorage.getItem(PR_CLIENT_LS) || ''); } catch { return ''; }
  });

  // ---- Right: warehouse/area + filters ----
  const [warehouses, setWarehouses] = useState([]); // [{id,name}]
  const [warehouseId, setWarehouseId] = useState(() => {
    try { return String(localStorage.getItem(WH_PICK_LS) || ''); } catch { return ''; }
  });
  const [areas, setAreas] = useState([]); // [{id,name}]
  const [areaId, setAreaId] = useState(() => {
    try { return String(localStorage.getItem(AREA_PICK_LS) || ''); } catch { return ''; }
  });

  const [rows, setRows] = useState([]);      // distinct aisles for dropdown
  const [bays, setBays] = useState([]);      // distinct columns
  const [shelves, setShelves] = useState([]);
  const [slots, setSlots] = useState([]);

  const [selRow, setSelRow] = useState('');
  const [selBay, setSelBay] = useState('');
  const [selShelf, setSelShelf] = useState('');
  const [selSlot, setSelSlot] = useState('');

  const [pickRows, setPickRows] = useState([]); // right table: available picking locations
  const [pickLoading, setPickLoading] = useState(false);
  const [selectedInvId, setSelectedInvId] = useState(null);
  const [selectedPickKey, setSelectedPickKey] = useState('');
  const [pickStatus, setPickStatus] = useState('all'); // 'available' | 'occupied' | 'all'
  const [refreshTick, setRefreshTick] = useState(0);
  const [selectedLocIds, setSelectedLocIds] = useState(() => new Set()); // checkbox selection for locations table

  // DnD move dialog state
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveSrc, setMoveSrc] = useState(null);     // { inventoryid, receiptid, clientid, sku, piecesonhand, qtyperunit }
  const [moveDest, setMoveDest] = useState(null);   // picking row { locationid, roww, columnn, shelve, bin }
  const [moveQty, setMoveQty] = useState('');
  const [moveErr, setMoveErr] = useState('');
  const [moveSrcOptions, setMoveSrcOptions] = useState([]); // when dragging from picking -> choose SKU
  // Storage unit types (picking only)
  const [storageTypes, setStorageTypes] = useState([]);
  const [selectedStorageTypeId, setSelectedStorageTypeId] = useState('');
  const [storageTypeLocked, setStorageTypeLocked] = useState(false);

  // Topbar title like other views
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('topbar:setTitle', { detail: { title: 'Picking Replenishment', subtitle: '' } }));
    return () => { window.dispatchEvent(new CustomEvent('topbar:clearTitle')); };
  }, []);

  // Helper: format primary SKU × pieces from range-with-pallets payload
  const formatSkuQty = (r) => {
    try {
      const skusStr = (r?.skus || '').toString().trim();
      let skuPiecesStr = (r?.sku_pieces || '').toString().trim();
      const primarySku = skusStr ? skusStr.split(',')[0].trim() : '';
      let piecesMap = new Map();
      if (skuPiecesStr) {
        skuPiecesStr.split('|').forEach(pair => {
          const [s, q] = String(pair || '').split(':');
          const n = Number(q);
          if (s && Number.isFinite(n)) piecesMap.set(s.trim(), n);
        });
      }
      // Fallback: if sku_pieces is empty, try sku_qtys (units + pieces) as a best-effort
      if (!piecesMap.size) {
        const skuQtysStr = (r?.sku_qtys || '').toString().trim();
        if (skuQtysStr) {
          skuQtysStr.split('|').forEach(pair => {
            const [s, q] = String(pair || '').split(':');
            const n = Number(q);
            if (s && Number.isFinite(n)) piecesMap.set(s.trim(), n);
          });
        }
      }
      const qty = primarySku && piecesMap.has(primarySku)
        ? piecesMap.get(primarySku)
        : (piecesMap.size ? Array.from(piecesMap.values())[0] : null);
      if (primarySku && qty != null) return `${primarySku} × ${qty}`;
      if (primarySku) return primarySku;
      if (qty != null) return `Qty ${qty}`;
      return '';
    } catch { return ''; }
  };

  // Resolve primary SKU (first in list) from a picking row
  const getPrimarySku = (r) => {
    try {
      const skusStr = (r?.skus || '').toString().trim();
      const s = skusStr ? skusStr.split(',')[0].trim() : '';
      return s || '';
    } catch { return ''; }
  };

  // Image preview (like Inventory.jsx)
  const [preview, setPreview] = useState({ src: '', x: 0, y: 0, visible: false });
  const previewRef = useRef(null);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const placePreview = (clientX, clientY) => {
    const vw = window.innerWidth, vh = window.innerHeight;
    const margin = 12, offset = 16;
    const boxW = previewRef.current?.offsetWidth || Math.min(vw * 0.4, 520);
    const boxH = previewRef.current?.offsetHeight || Math.min(vh * 0.6, 520);
    let left = clientX + offset, top = clientY + offset;
    if (left + boxW + margin > vw) left = clientX - boxW - offset;
    if (top + boxH + margin > vh) top = clientY - boxH - offset;
    if (left < margin) left = margin;
    if (top < margin) top = margin;
    if (left + boxW + margin > vw) left = vw - boxW - margin;
    if (top + boxH + margin > vh) top = vh - boxH - margin;
    setPreview(p => ({ ...p, x: left, y: top }));
  };
  const showPreview = (raw, e) => {
    const src = raw || resolveItemThumb({ itemimageurl: raw, image1: raw });
    if (!src) return;
    lastPosRef.current = { x: e.clientX, y: e.clientY };
    setPreview({ src, x: e.clientX, y: e.clientY, visible: true });
    requestAnimationFrame(() => placePreview(e.clientX, e.clientY));
  };
  const movePreview = (e) => {
    if (!preview.visible) return;
    lastPosRef.current = { x: e.clientX, y: e.clientY };
    placePreview(e.clientX, e.clientY);
  };
  const hidePreview = () => setPreview(p => ({ ...p, visible: false }));
  const onPreviewImgLoad = () => {
    if (!preview.visible) return;
    const { x, y } = lastPosRef.current;
    placePreview(x, y);
  };

  // Resolve a thumbnail from common fields (URL / data URL / base64 / Buffer-like)
  const resolveItemThumb = (row) => {
    const isDataUrl = (s) => typeof s === 'string' && s.trim().startsWith('data:image/');
    const isHttpUrl = (s) => typeof s === 'string' && (/^https?:\/\//i.test(s) || s.startsWith('/'));
    const isB64 = (s) => typeof s === 'string' && s.replace(/\s+/g, '').length > 64 && /^[A-Za-z0-9+/=]+$/.test(s.replace(/\s+/g, ''));

    // 1) URL-ish fields first
    const urlFields = ['itemimageurl','item_image_url','imageurl','image_url','imageUrl','thumb','thumbnail'];
    for (const f of urlFields) {
      const v = row?.[f];
      if (v && (isHttpUrl(v) || isDataUrl(v))) return String(v).trim();
    }

    // 2) Common base64/buffer fields
    const baseFields = ['image1','itemimage1','image','img','image_base64','imageB64'];
    for (const f of baseFields) {
      const v = row?.[f]; if (!v) continue;
      if (typeof v === 'string') {
        const s = v.trim();
        if (isDataUrl(s)) return s;
        if (isB64(s)) return `data:image/jpeg;base64,${s.replace(/\s+/g, '')}`;
      }
      if (v && typeof v === 'object') {
        const d = Array.isArray(v?.data) ? v.data : (Array.isArray(v) ? v : null);
        if (d) {
          try { const u8 = new Uint8Array(d); const blob = new Blob([u8], { type: 'image/jpeg' }); return URL.createObjectURL(blob); } catch {}
        }
      }
    }
    return '';
  };

  // (No per-SKU fetcher; thumbnails resolve from API payload fields like Inventory.jsx)

  const renderClientBadge = (r) => {
    try {
      const s = (r?.clients || '').toString().trim();
      if (!s) return null;
      // Show full customer name(s); if multiple, join by comma and allow wrapping
      const parts = s.split(',').map(t => t.trim()).filter(Boolean);
      const full = parts.join(', ');
      return <span className="client-badge" title={full}>{full}</span>;
    } catch { return null; }
  };

  // ====== Load warehouses on mount ======
  useEffect(() => {
    (async () => {
      try {
        const list = await fetchJSON(`${API}/api/warehouses`);
        const arr = Array.isArray(list) ? list.map(w => ({ id: String(w.id), name: w.name })) : [];
        setWarehouses(arr);
        if (!warehouseId && arr.length) setWarehouseId(String(arr[0].id));
      } catch { setWarehouses([]); }
    })();
    // Load client options for left-side filter
    (async () => {
      try {
        const j = await fetchJSON(`${API}/api/clients?limit=1000`);
        const list = Array.isArray(j?.data) ? j.data : [];
        setClientOptions(list.map(c => ({ id: String(c.id), name: c.name })));
      } catch { setClientOptions([]); }
    })();
  }, []);

  // Load picking storage unit types (picktype=1), scoped by client when available
  useEffect(() => {
    (async () => {
      try {
        const cid = Number(clientId || 0) || null;
        let url;
        if (cid) {
          const qs = new URLSearchParams();
          qs.set('clientid', String(cid));
          qs.set('pick', '1');
          url = `${API}/api/clientstorageunits?${qs.toString()}`;
        } else {
          url = `${API}/api/storageunits?pick=1`;
        }
        let r = await fetch(url);
        if (!r.ok && cid) {
          // Fallback to global storageunits if client-specific endpoint fails
          r = await fetch(`${API}/api/storageunits?pick=1`);
        }
        const j = await r.json();
        const arr = Array.isArray(j) ? j : [];
        setStorageTypes(arr);
        if (!selectedStorageTypeId && arr.length) setSelectedStorageTypeId(String(arr[0].id));
      } catch {
        setStorageTypes([]);
      }
    })();
  }, [clientId, selectedStorageTypeId]);

  // Persist warehouse/area
  useEffect(() => {
    try { if (warehouseId) localStorage.setItem(WH_PICK_LS, String(warehouseId)); } catch {}
  }, [warehouseId]);
  useEffect(() => {
    try { if (areaId) localStorage.setItem(AREA_PICK_LS, String(areaId)); } catch {}
  }, [areaId]);

  // Load areas for selected warehouse
  useEffect(() => {
    if (!warehouseId) return;
    (async () => {
      try {
        const list = await fetchJSON(`${API}/api/warehouseareas/warehouseareas?warehouseid=${warehouseId}&picking=1`);
        const arr = Array.isArray(list) ? list : [];
        setAreas(arr);
        if (!areaId || !arr.some(a => String(a.id) === String(areaId))) setAreaId(String(arr[0]?.id || ''));
      } catch { setAreas([]); }
    })();
  }, [warehouseId]);

  // Load dropdown options based on selections
  useEffect(() => {
    const toastId = TOAST_IDS.aisles;
    let cancelled = false;
    const stopToast = () => hideToast(toastId);
    // Rows (aisles)
    if (!warehouseId || !areaId) { setRows([]); setSelRow(''); stopToast(); return; }
    showToast({ id: toastId, message: 'Loading aisles…', type: 'info', duration: 0, align: 'center', pill: true });
    (async () => {
      try {
        const data = await fetchJSON(`${API}/api/locations/aisles?warehouseid=${warehouseId}&areaid=${areaId}`);
        if (cancelled) return;
        const vals = (Array.isArray(data) ? data : [])
          .map(r => r.roww)
          .filter(v => v != null)
          .map(v => String(v));
        setRows(vals);
        if (selRow && !vals.includes(String(selRow))) setSelRow('');
      } catch {
        if (!cancelled) {
          setRows([]); setSelRow('');
        }
      } finally {
        if (!cancelled) stopToast();
      }
    })();
    return () => {
      cancelled = true;
      stopToast();
    };
  }, [warehouseId, areaId]);

  useEffect(() => {
    const toastId = TOAST_IDS.bays;
    let cancelled = false;
    const stopToast = () => hideToast(toastId);
    // Bays depend on row
    if (!warehouseId || !areaId || !selRow) { setBays([]); setSelBay(''); stopToast(); return; }
    showToast({ id: toastId, message: 'Loading columns…', type: 'info', duration: 0, align: 'center', pill: true });
    (async () => {
      try {
        const data = await fetchJSON(`${API}/api/locations/bays?warehouseid=${warehouseId}&areaid=${areaId}&aisle=${selRow}`);
        if (cancelled) return;
        const vals = (Array.isArray(data) ? data : [])
          .map(r => r.bay)
          .filter(v => v != null)
          .map(v => String(v));
        setBays(vals);
        if (selBay && !vals.includes(String(selBay))) setSelBay('');
      } catch {
        if (!cancelled) { setBays([]); setSelBay(''); }
      } finally {
        if (!cancelled) stopToast();
      }
    })();
    return () => { cancelled = true; stopToast(); };
  }, [warehouseId, areaId, selRow]);

  useEffect(() => {
    const toastId = TOAST_IDS.shelves;
    let cancelled = false;
    const stopToast = () => hideToast(toastId);
    // Shelves depend on row+bay
    if (!warehouseId || !areaId || !selRow || !selBay) { setShelves([]); setSelShelf(''); stopToast(); return; }
    showToast({ id: toastId, message: 'Loading shelves…', type: 'info', duration: 0, align: 'center', pill: true });
    (async () => {
      try {
        const data = await fetchJSON(`${API}/api/locations/shelves?warehouseid=${warehouseId}&areaid=${areaId}&aisle=${selRow}&bay=${selBay}`);
        if (cancelled) return;
        const vals = (Array.isArray(data) ? data : [])
          .map(r => r.shelf)
          .filter(v => v != null)
          .map(v => String(v));
        setShelves(vals);
        if (selShelf && !vals.includes(String(selShelf))) setSelShelf('');
      } catch {
        if (!cancelled) { setShelves([]); setSelShelf(''); }
      } finally {
        if (!cancelled) stopToast();
      }
    })();
    return () => { cancelled = true; stopToast(); };
  }, [warehouseId, areaId, selRow, selBay]);

  useEffect(() => {
    const toastId = TOAST_IDS.slots;
    let cancelled = false;
    const stopToast = () => hideToast(toastId);
    // Slots depend on row+bay+shelf
    if (!warehouseId || !areaId || !selRow || !selBay || !selShelf) { setSlots([]); setSelSlot(''); stopToast(); return; }
    showToast({ id: toastId, message: 'Loading slots…', type: 'info', duration: 0, align: 'center', pill: true });
    (async () => {
      try {
        const data = await fetchJSON(`${API}/api/locations/slots?warehouseid=${warehouseId}&areaid=${areaId}&aisle=${selRow}&bay=${selBay}&shelf=${selShelf}`);
        if (cancelled) return;
        const vals = (Array.isArray(data) ? data : [])
          .map(r => r.slot)
          .filter(v => v != null)
          .map(v => String(v));
        setSlots(vals);
        if (selSlot && !vals.includes(String(selSlot))) setSelSlot('');
      } catch {
        if (!cancelled) { setSlots([]); setSelSlot(''); }
      } finally {
        if (!cancelled) stopToast();
      }
    })();
    return () => { cancelled = true; stopToast(); };
  }, [warehouseId, areaId, selRow, selBay, selShelf]);

  // Fetch available picking locations whenever any dropdown changes
  useEffect(() => {
    const toastId = TOAST_IDS.locations;
    let cancelled = false;
    const stopToast = () => hideToast(toastId);
    if (!warehouseId || !areaId) { setPickRows([]); stopToast(); return; }
    setPickLoading(true);
    showToast({ id: toastId, message: 'Loading picking locations…', type: 'info', duration: 0, align: 'center', pill: true });
    (async () => {
      try {
        // Determine aisle range: if no specific aisle selected, span all known aisles
        const aisleVals = Array.isArray(rows) ? rows.map(Number).filter(Number.isFinite) : [];
        const aStart = selRow ? Number(selRow) : (aisleVals.length ? Math.min(...aisleVals) : 1);
        const aEnd   = selRow ? Number(selRow) : (aisleVals.length ? Math.max(...aisleVals) : aStart);

        // Determine bay range: if a bay isn’t selected, span all known bays (for selected aisle) or a wide default
        const bayVals = Array.isArray(bays) ? bays.map(Number).filter(Number.isFinite) : [];
        const bStart = selBay ? Number(selBay) : (bayVals.length ? Math.min(...bayVals) : 1);
        const bEnd   = selBay ? Number(selBay) : (bayVals.length ? Math.max(...bayVals) : 999999);

        const qs = new URLSearchParams({
          warehouseid: String(warehouseId),
          areaid: String(areaId),
          aisleStart: String(aStart),
          aisleEnd: String(aEnd),
          bayStart: String(bStart),
          bayEnd: String(bEnd)
        });
        const data = await fetchJSON(`${API}/api/locations/range-with-pallets?${qs.toString()}`);
        if (cancelled) return;
        const arr = Array.isArray(data) ? data : [];
        // filter: type 3 or 6 (picking); status by pickStatus; also match shelf/slot when set
        const out = arr.filter(r => {
          const t = Number(r.type);
          const isPick = (t === 3 || t === 6);
          if (!isPick) return false;
          const inUse = Number(r.in_use) > 0;
          if (pickStatus === 'available' && inUse) return false;
          if (pickStatus === 'occupied' && !inUse) return false;
          if (selShelf && String(r.shelve) !== String(selShelf)) return false;
          if (selSlot && String(r.bin) !== String(selSlot)) return false;
          return true;
        });
        setPickRows(out);
      } catch {
        if (!cancelled) setPickRows([]);
      } finally {
        if (!cancelled) {
          setPickLoading(false);
          stopToast();
        }
      }
    })();
    return () => {
      cancelled = true;
      setPickLoading(false);
      stopToast();
    };
  }, [warehouseId, areaId, selRow, selBay, selShelf, selSlot, rows, bays, pickStatus, refreshTick]);

  // Prune checkbox selection when the visible list changes
  useEffect(() => {
    setSelectedLocIds(prev => {
      if (!prev || prev.size === 0) return prev;
      const visible = new Set(pickRows.map(r => Number(r.locationid)).filter(Number.isFinite));
      const next = new Set(Array.from(prev).filter(id => visible.has(Number(id))));
      return next;
    });
  }, [pickRows]);

  // Persist left filters
  useEffect(() => { try { localStorage.setItem(PR_CLIENT_LS, clientId); } catch {} }, [clientId]);
  useEffect(() => { try { localStorage.setItem(PR_SKUQ_LS, skuQuery); } catch {} }, [skuQuery]);

  // Debounced left search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const q = skuQuery.trim();
      if (!q) { setInvRows([]); return; }
      setInvLoading(true);
      try {
        // Use inventory available endpoint; search by sku or general q
        const params = new URLSearchParams();
        if (q.length >= 2) params.set('q', q);
        else params.set('sku', q);
        params.set('limit', '200');
        if (clientId) params.set('clientid', clientId);
        const url = `${API}/api/inventory/available?${params.toString()}`;
        const { data } = await fetchJSON(url);
        const list = Array.isArray(data) ? data : [];
        // Filter out inventory from picking bins (locations.type = 6)
        const filtered = list.filter(r => Number(r.locationtype || 0) !== 6);
        setInvRows(filtered);
      } catch {
        setInvRows([]);
      } finally {
        setInvLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [skuQuery]);

  // Helper to refresh left inventory immediately (used after moves)
  const refreshLeftNow = async () => {
    const q = skuQuery.trim();
    if (!q) { setInvRows([]); return; }
    setInvLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.length >= 2) params.set('q', q);
      else params.set('sku', q);
      params.set('limit', '200');
      if (clientId) params.set('clientid', clientId);
      const url = `${API}/api/inventory/available?${params.toString()}`;
      const { data } = await fetchJSON(url);
      const list = Array.isArray(data) ? data : [];
      const filtered = list.filter(r => Number(r.locationtype || 0) !== 6);
      setInvRows(filtered);
    } catch {
      setInvRows([]);
    } finally {
      setInvLoading(false);
    }
  };

  // ----- DnD helpers -----
  const onDragStartInv = (e, r) => {
    try {
      const payload = {
        kind: 'inventory',
        inventoryid: r.inventoryid,
        receiptid: r.receiptid,
        clientid: r.clientid,
        sku: r.sku,
        piecesonhand: Number(r.piecesonhand || 0),
        qtyperunit: Number(r.qtyperunit || 0)
      };
      const s = JSON.stringify(payload);
      // Some browsers (Safari/iOS) only allow certain MIME types; set multiple for compatibility
      try { e.dataTransfer.setData('application/json', s); } catch {}
      try { e.dataTransfer.setData('text/plain', s); } catch {}
      try { e.dataTransfer.setData('text', s); } catch {}
      e.dataTransfer.effectAllowed = 'copy';
      dragPayloadRef.current = payload;
    } catch {}
  };
  const onDragStartPick = (e, row) => {
    try {
      // Derive client id if exactly one client present at this bin
      const idsStr = (row.client_ids || '').toString().trim();
      let dragClientId = null;
      if (idsStr) {
        const ids = idsStr.split(',').map(s => Number(s)).filter(Number.isFinite);
        if (ids.length === 1) dragClientId = ids[0];
      }
      const payload = {
        kind: 'picking',
        locationid: row.locationid,
        warehouseid: row.warehouseid,
        areaid: row.areaid,
        roww: row.roww,
        columnn: row.columnn,
        shelve: row.shelve,
        bin: row.bin,
        clientid: dragClientId,
      };
      const s = JSON.stringify(payload);
      try { e.dataTransfer.setData('application/json', s); } catch {}
      try { e.dataTransfer.setData('text/plain', s); } catch {}
      try { e.dataTransfer.setData('text', s); } catch {}
      e.dataTransfer.effectAllowed = 'copyMove';
      dragPayloadRef.current = payload;
    } catch {}
  };
  const parseDnd = (e) => {
    try {
      let t = e.dataTransfer.getData('application/json');
      if (!t) t = e.dataTransfer.getData('text/plain');
      if (!t) t = e.dataTransfer.getData('text');
      const json = t ? JSON.parse(t) : null;
      if (json && json.kind === 'inventory') return json;
    } catch {}
    return null;
  };
  const [hoverKey, setHoverKey] = useState('');
  // Firefox does not expose dataTransfer.getData in dragover/dragenter; keep a ref
  const dragPayloadRef = useRef(null);
  const canDropHere = (row, payload) => {
    if (!payload) return false;
    const inUse = Number(row.in_use) > 0;
    if (!inUse) return true; // empty bin OK
    const idsStr = (row.client_ids || '').toString().trim();
    if (!idsStr) return true;
    const ids = idsStr.split(',').map(s => Number(s)).filter(Number.isFinite);
    if (!ids.length) return true;
    const dragClient = Number(payload.clientid || 0) || null;
    if (!dragClient) return false;
    return ids.every(id => id === dragClient);
  };

  // ===== Left side: allow dropping FROM picking bin back to a WR (receipt/pallet) =====
  const [leftHoverInvId, setLeftHoverInvId] = useState(null);
  const onLeftDragOverRow = (row, e) => {
    const d = dragPayloadRef.current;
    if (d && d.kind === 'picking') {
      e.preventDefault();
      try { e.dataTransfer.dropEffect = 'copy'; } catch {}
    }
  };
  const onLeftDragEnterRow = (row, e) => {
    const d = dragPayloadRef.current;
    if (d && d.kind === 'picking') setLeftHoverInvId(row.inventoryid);
  };
  const onLeftDragLeaveRow = (row, e) => {
    setLeftHoverInvId(prev => (prev === row.inventoryid ? null : prev));
  };
  // Move dialog supports two modes: toPicking (right) or toReceipt (left)
  const [moveMode, setMoveMode] = useState('toPicking'); // 'toPicking' | 'toReceipt'
  const onLeftDropRow = async (row, e) => {
    e.preventDefault();
    const d = parseDnd(e) || dragPayloadRef.current;
    dragPayloadRef.current = null;
    setLeftHoverInvId(null);
    if (!d || d.kind !== 'picking') return;
    try {
      // Load possible source inventory at the source picking bin
      const params = new URLSearchParams();
      params.set('locationid', String(d.locationid));
      params.set('limit', '10000');
      if (d.clientid) params.set('clientid', String(d.clientid));
      const url = `${API}/api/inventory/available?${params.toString()}`;
      const j = await fetchJSON(url);
      const list = Array.isArray(j?.data) ? j.data : [];
      if (!list.length) { alert('No inventory found at the source picking bin.'); return; }
      // Default to first row; allow change via selector in modal
      const first = list[0];
      const srcPayload = {
        kind: 'inventory',
        inventoryid: first.inventoryid,
        receiptid: first.receiptid,
        clientid: first.clientid,
        sku: first.sku,
        piecesonhand: Number(first.piecesonhand || 0),
        qtyperunit: Number(first.qtyperunit || 0)
      };
      setMoveSrc(srcPayload);
      setMoveSrcOptions(list.map(it => ({
        inventoryid: it.inventoryid,
        receiptid: it.receiptid,
        clientid: it.clientid,
        sku: it.sku,
        piecesonhand: Number(it.piecesonhand || 0),
        qtyperunit: Number(it.qtyperunit || 0)
      })));
      // Destination is the chosen WR row (receipt + pallet number)
      const destObj = {
        receiptid: row.receiptid,
        palletnumber: Number(row.pallet || row.pallet_number || 0) || null,
        inventoryid: row.inventoryid,
      };
      setMoveDest(destObj);
      setMoveMode('toReceipt');
      setStorageTypeLocked(false);
      setMoveQty(''); setMoveErr(''); setMoveOpen(true);
    } catch (err) {
      alert('Failed to load source bin contents.');
      return;
    }
  };
  const onDragOverLoc = (row, e) => {
    const payload = dragPayloadRef.current;
    if (payload && canDropHere(row, payload)) {
      e.preventDefault();
      try { e.dataTransfer.dropEffect = 'copy'; } catch {}
    }
  };
  const onDragEnterLoc = (row, key, e) => {
    const payload = dragPayloadRef.current;
    if (payload && canDropHere(row, payload)) setHoverKey(key);
  };
  const onDragLeaveLoc = (key, e) => { setHoverKey(prev => (prev === key ? '' : prev)); };
  const onDropLoc = async (r, key, e) => {
    e.preventDefault();
    // Prefer event payload; if missing (browser quirk), fall back to current ref
    const d = parseDnd(e) || dragPayloadRef.current;
    // Clear any lingering drag state now that we've obtained the payload
    dragPayloadRef.current = null;
    setHoverKey('');
    if (!d) return;
    if (!canDropHere(r, d)) {
      try { alert('Cannot move into a bin with a different client.'); } catch {}
      return;
    }
    // Determine move source and open modal
    const destObj = {
      locationid: r.locationid,
      roww: r.roww, columnn: r.columnn, shelve: r.shelve, bin: r.bin,
      areaid: areaId,
    };
    // If dragging from left inventory table
    if (d.kind === 'inventory') {
      setMoveSrc(d);
      setMoveSrcOptions([]);
      setMoveDest(destObj);
      setMoveMode('toPicking');
      const occupied = Number(r.in_use) > 0;
      const suId = Number(r.storageunitid || 0) || null;
      if (occupied && suId) { setSelectedStorageTypeId(String(suId)); setStorageTypeLocked(true); }
      else { setStorageTypeLocked(false); }
      setMoveQty(''); setMoveErr(''); setMoveOpen(true);
      dragPayloadRef.current = null;
      return;
    }
    // Dragging from another picking bin
    if (d.kind === 'picking') {
      try {
        // Fetch available inventory at the source picking location (stack 1)
        const params = new URLSearchParams();
        params.set('locationid', String(d.locationid));
        params.set('limit', '10000');
        if (d.clientid) params.set('clientid', String(d.clientid));
        const url = `${API}/api/inventory/available?${params.toString()}`;
        const j = await fetchJSON(url);
        const list = Array.isArray(j?.data) ? j.data : [];
        if (!list.length) { alert('No inventory found at the source picking bin.'); return; }
        // Default to first row; allow change via selector in modal
        const first = list[0];
        const srcPayload = {
          kind: 'inventory',
          inventoryid: first.inventoryid,
          receiptid: first.receiptid,
          clientid: first.clientid,
          sku: first.sku,
          piecesonhand: Number(first.piecesonhand || 0),
          qtyperunit: Number(first.qtyperunit || 0)
        };
        setMoveSrc(srcPayload);
        setMoveSrcOptions(list.map(it => ({
          inventoryid: it.inventoryid,
          receiptid: it.receiptid,
          clientid: it.clientid,
          sku: it.sku,
          piecesonhand: Number(it.piecesonhand || 0),
          qtyperunit: Number(it.qtyperunit || 0)
        })));
        setMoveDest(destObj);
        setMoveMode('toPicking');
        const occupied = Number(r.in_use) > 0;
        const suId = Number(r.storageunitid || 0) || null;
        if (occupied && suId) { setSelectedStorageTypeId(String(suId)); setStorageTypeLocked(true); }
        else { setStorageTypeLocked(false); }
        setMoveQty(''); setMoveErr(''); setMoveOpen(true);
      } catch (err) {
        alert('Failed to load source bin contents.');
        return;
      } finally {
        dragPayloadRef.current = null;
      }
      return;
    }
  };

  const user = useMemo(() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } }, []);
  const formatLocLabel = (r) => `${pad3(r.roww)}-${pad3(r.columnn)}-${pad2(r.shelve)}-${pad2(r.bin)}`;
  const genDestPalletNumber = (receiptid, locationid) => {
    const base = Number(locationid) || 0;
    return 900000 + (base % 100000); // simple stable mapping
  };
  const doMove = async () => {
    const qty = Number(moveQty);
    if (!moveSrc || !moveDest) return;
    if (!Number.isFinite(qty) || qty <= 0) { setMoveErr('Enter a positive quantity.'); return; }
    if (qty > Number(moveSrc.piecesonhand || 0)) { setMoveErr(`Cannot move more than on hand (${moveSrc.piecesonhand}).`); return; }
    let payload;
    if (moveMode === 'toPicking') {
      if (!selectedStorageTypeId) { setMoveErr('Choose a Storage Type.'); return; }
      payload = {
        source_inventoryid: Number(moveSrc.inventoryid),
        clientid: Number(moveSrc.clientid || 0),
        items_per_unit: Number(moveSrc.qtyperunit || 0) || null,
        move_cases: 0,
        move_opened_cases: 0,
        move_loose_items: qty,
        dest_receiptid: Number(moveSrc.receiptid),
        dest_pallet_number: genDestPalletNumber(moveSrc.receiptid, moveDest.locationid),
        username: user?.fullname || 'system',
        userid: Number(user?.userid || 0) || null,
        move_to_picking: true,
        picking_areaid: Number(moveDest.areaid || 0) || null,
        picking_locationid: Number(moveDest.locationid || 0) || null,
        dest_storageunitid: Number(selectedStorageTypeId) || null,
      };
    } else {
      // toReceipt: move from picking bin back to an existing WR pallet
      payload = {
        source_inventoryid: Number(moveSrc.inventoryid),
        clientid: Number(moveSrc.clientid || 0),
        items_per_unit: Number(moveSrc.qtyperunit || 0) || null,
        move_cases: 0,
        move_opened_cases: 0,
        move_loose_items: qty,
        dest_receiptid: Number(moveDest.receiptid || moveSrc.receiptid),
        dest_pallet_number: Number(moveDest.palletnumber || 0) || 1,
        username: user?.fullname || 'system',
        userid: Number(user?.userid || 0) || null,
        move_to_picking: false,
        picking_areaid: null,
        picking_locationid: null,
        dest_storageunitid: null,
      };
    }
    try {
      const res = await fetch(`${API}/api/inventory/move`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.success === false) throw new Error(j?.error || `Move failed (${res.status})`);
      // local update: adjust counts
      if (moveMode === 'toPicking') {
        // Reduce pieces on left table for this source row
        setInvRows(prev => prev.map(row => (
          row.inventoryid === moveSrc.inventoryid ? { ...row, piecesonhand: Math.max(0, Number(row.piecesonhand || 0) - qty) } : row
        )));
      } else {
        // Increase pieces on left table for the destination row if present
        setInvRows(prev => prev.map(row => (
          row.inventoryid === (moveDest.inventoryid || 0) ? { ...row, piecesonhand: Number(row.piecesonhand || 0) + qty } : row
        )));
      }
      setMoveOpen(false);
      setMoveSrc(null); setMoveDest(null); setMoveQty('');
      setMoveSrcOptions([]);
      setRefreshTick(t => t + 1);
      // Keep both sides in sync after move
      refreshLeftNow().catch(() => {});
    } catch (e) {
      setMoveErr(e?.message || 'Move failed');
    }
  };

  // ---- Render ----
  return (
    <div className="pr-shell">
      {/* Controls toolbar (above split) so both tables start aligned */}
      <div className="pr-toolbar">
        <div className="pr-toolbar-left" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {/* Client filter styled like Receipts search */}
          <div className="search" style={{ minWidth: 260 }}>
            <FaSearch />
            <select
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              style={{ flex: 1, background: 'transparent', border: 'none', color: 'var(--text-color)', height: 36, outline: 'none' }}
              title="Filter by client"
            >
              <option value="">All Clients</option>
              {clientOptions.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {clientId && (
              <button className="clear" onClick={() => setClientId('')}><FaTimes /></button>
            )}
          </div>
          {/* SKU query styled like Receipts search */}
          <div className="search" style={{ minWidth: 280 }}>
            <FaSearch />
            <input
              type="text"
              placeholder="Search by SKU or description…"
              value={skuQuery}
              onChange={(e) => setSkuQuery(e.target.value)}
            />
            {skuQuery && (
              <button className="clear" onClick={() => setSkuQuery('')}><FaTimes /></button>
            )}
          </div>
        </div>
        <div className="pr-toolbar-right">
          <div className="pr-controls" style={{ marginBottom: 0 }}>
            <div className="field">
              <label>Warehouse</label>
              <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
                {warehouses.map(w => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Area</label>
              <select value={areaId} onChange={(e) => setAreaId(e.target.value)}>
                {areas.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Row (Aisle)</label>
              <select value={selRow} onChange={(e) => setSelRow(e.target.value)}>
                <option value="">All</option>
                {rows.map(v => <option key={v} value={v}>{pad3(v)}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Column (Bay)</label>
              <select value={selBay} onChange={(e) => setSelBay(e.target.value)} disabled={!selRow}>
                <option value="">All</option>
                {bays.map(v => <option key={v} value={v}>{pad3(v)}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Shelve</label>
              <select value={selShelf} onChange={(e) => setSelShelf(e.target.value)} disabled={!selBay}>
                <option value="">All</option>
                {shelves.map(v => <option key={v} value={v}>{pad2(v)}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Slot</label>
              <select value={selSlot} onChange={(e) => setSelSlot(e.target.value)} disabled={!selShelf}>
                <option value="">All</option>
                {slots.map(v => <option key={v} value={v}>{pad2(v)}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Status</label>
              <select value={pickStatus} onChange={(e) => setPickStatus(e.target.value)}>
                <option value="available">Available</option>
                <option value="occupied">Occupied</option>
                <option value="all">All</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <Split className="pr-split" sizes={[50,50]} minSize={200} gutterSize={6}>
        {/* LEFT: SKU results */}
        <div className="pr-pane">
          <div className="pr-table-wrap">
            <table className="pr-table">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>WR#</th>
                  <th>Storage Location</th>
                  <th style={{ width: 64 }}>Image</th>
                  <th style={{ width: 180 }}>SKU</th>
                  <th style={{ width: 120 }}>Qty Avail</th>
                </tr>
              </thead>
              <tbody>
                {invLoading ? (
                  <tr><td colSpan={5}>Loading…</td></tr>
                ) : invRows.length === 0 ? (
                  <tr><td colSpan={5}>No results</td></tr>
                ) : invRows.map(r => (
                  <tr
                    key={r.inventoryid}
                    draggable
                    onDragStart={(e) => onDragStartInv(e, r)}
                    onDragEnd={() => { dragPayloadRef.current = null; }}
                    onDragOver={(e) => onLeftDragOverRow(r, e)}
                    onDragEnter={(e) => onLeftDragEnterRow(r, e)}
                    onDragLeave={(e) => onLeftDragLeaveRow(r, e)}
                    onDrop={(e) => onLeftDropRow(r, e)}
                    className={`${selectedInvId === r.inventoryid ? 'active ' : ''}${leftHoverInvId === r.inventoryid ? ' dropping ' : ''}`}
                    onClick={() => setSelectedInvId(r.inventoryid)}
                    style={{ cursor: 'grab' }}
                  >
                    <td>{r.receiptid}</td>
                    <td>{r.location || ''}</td>
                    <td>
                      {(() => {
                        const src = resolveItemThumb(r);
                        return src ? (
                          <img
                            src={src}
                            alt={r.sku || 'thumb'}
                            className="pr-thumb"
                            loading="lazy"
                            onMouseEnter={(e) => showPreview(r.image1 || r.itemimageurl || src, e)}
                            onMouseMove={movePreview}
                            onMouseLeave={hidePreview}
                          />
                        ) : (<span className="muted">—</span>);
                      })()}
                    </td>
                    <td>{r.sku}</td>
                    <td>{Number(r.piecesonhand ?? 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.visible && preview.src && (
              <div className="pr-preview" ref={previewRef} style={{ left: preview.x, top: preview.y }}>
                <img src={preview.src} alt="" onLoad={onPreviewImgLoad} />
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: available picking locations */}
        <div className="pr-pane">
          {/* Locations toolbar above the table */}
          <div className="pr-loc-toolbar">
            <button
              className="btn"
              disabled={pickLoading || selectedLocIds.size === 0}
              title={selectedLocIds.size ? `Clear ${selectedLocIds.size} selected location(s)` : 'Select location(s) to enable'}
              onClick={async () => {
                if (selectedLocIds.size === 0) return;
                const ok = window.confirm(`Clear ${selectedLocIds.size} selected location(s)? Pallets will be moved to Floor.`);
                if (!ok) return;
                try {
                  const ids = Array.from(selectedLocIds);
                  await Promise.all(ids.map(async (locId) => {
                    try {
                      await fetchJSON(`${API}/api/mobile/locations/clear-location`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ locationid: locId })
                      });
                    } catch {}
                  }));
                } finally {
                  setSelectedLocIds(new Set());
                  setRefreshTick(t => t + 1);
                }
              }}
            >
              Clear Location(s)
            </button>
          </div>
          <div className="pr-table-wrap">
            <table className="pr-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <input
                      type="checkbox"
                      aria-label="Select all locations"
                      checked={pickRows.length > 0 && pickRows.every(r => selectedLocIds.has(Number(r.locationid)))}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelectedLocIds(prev => {
                          if (!checked) return new Set();
                          const next = new Set();
                          pickRows.forEach(r => { if (r.locationid) next.add(Number(r.locationid)); });
                          return next;
                        });
                      }}
                    />
                  </th>
                  <th style={{ width: 64 }}>Row</th>
                  <th style={{ width: 64 }}>Column</th>
                  <th style={{ width: 56 }}>Shelve</th>
                  <th style={{ width: 56 }}>Slot</th>
                  <th style={{ width: 160 }}>Storage Type</th>
                  <th>Location</th>
                  <th style={{ width: 220 }}>SKU / Qty</th>
                  <th style={{ width: 90 }}>Image</th>
                </tr>
              </thead>
              <tbody>
                {pickLoading ? (
                  <tr><td colSpan={9}>Loading…</td></tr>
                ) : pickRows.length === 0 ? (
                  <tr><td colSpan={9}>No available picking locations</td></tr>
                ) : pickRows.map((r) => {
                  const key = `${r.locationid || ''}|${r.roww}|${r.columnn}|${r.shelve}|${r.bin}`;
                  // Disable row if occupied by different client than toolbar-selected client
                  const idsStr = (r.client_ids || '').toString().trim();
                  const idsArr = idsStr ? idsStr.split(',').map(s => Number(s)).filter(Number.isFinite) : [];
                  const occupied = Number(r.in_use) > 0;
                  const toolbarClient = Number(clientId || 0) || null;
                  const disabledByToolbar = occupied && toolbarClient && idsArr.length > 0 && idsArr.some(id => id !== toolbarClient);
                  return (
                  <tr
                    key={key}
                    className={`${selectedPickKey === key ? 'active ' : ''}${disabledByToolbar ? 'disabled ' : ''}${hoverKey === key ? ' dropping ' : ''}`}
                    draggable
                    onDragStart={(e) => onDragStartPick(e, r)}
                    onDragEnd={() => { dragPayloadRef.current = null; }}
                    onDragOver={(e) => onDragOverLoc(r, e)}
                    onDragEnter={(e) => onDragEnterLoc(r, key, e)}
                    onDragLeave={(e) => onDragLeaveLoc(key, e)}
                    onDrop={(e) => { e.preventDefault(); onDropLoc(r, key, e); }}
                    onClick={() => { if (!disabledByToolbar) setSelectedPickKey(key); }}
                    style={{ cursor: disabledByToolbar ? 'not-allowed' : 'pointer' }}
                  >
                    <>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Select location ${r.locationlabel || ''}`}
                        checked={selectedLocIds.has(Number(r.locationid))}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          const idNum = Number(r.locationid);
                          setSelectedLocIds(prev => {
                            const next = new Set(prev);
                            if (checked) next.add(idNum); else next.delete(idNum);
                            return next;
                          });
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td>{pad3(r.roww)}</td>
                    <td>{pad3(r.columnn)}</td>
                    <td>{pad2(r.shelve)}</td>
                    <td>{pad2(r.bin)}</td>
                    <td>{r.storageunitname || ''}</td>
                    <td>
                      {r.locationlabel || `${r.roww}-${r.columnn}-${r.shelve}-${r.bin}`}
                      {renderClientBadge(r)}
                    </td>
                    <td>{formatSkuQty(r)}</td>
                    <td>
                      {(() => {
                        const src = resolveItemThumb(r);
                        return src ? (
                          <img
                            src={src}
                            alt={getPrimarySku(r) || 'thumb'}
                            className="pr-thumb"
                            loading="lazy"
                            onMouseEnter={(e) => showPreview(src, e)}
                            onMouseMove={movePreview}
                            onMouseLeave={hidePreview}
                          />
                        ) : (<span className="muted">—</span>);
                      })()}
                    </td>
                    </>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        </div>
      </Split>

      {moveOpen && (
        <div className="pr-modal-backdrop" onClick={() => { setMoveOpen(false); setMoveSrcOptions([]); }}>
          <div className="pr-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{moveMode === 'toPicking' ? 'Move to Picking' : 'Move to Receipt'}</h3>
            <div className="row">
              <div>
                <div className="muted">SKU</div>
                {Array.isArray(moveSrcOptions) && moveSrcOptions.length > 1 ? (
                  <select
                    value={String(moveSrc?.inventoryid || '')}
                    onChange={(e) => {
                      const invId = Number(e.target.value);
                      const opt = moveSrcOptions.find(o => o.inventoryid === invId);
                      if (opt) setMoveSrc({ kind: 'inventory', ...opt });
                    }}
                  >
                    {moveSrcOptions.map(o => (
                      <option key={o.inventoryid} value={o.inventoryid}>
                        {o.sku} — On Hand: {o.piecesonhand}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div>{moveSrc?.sku || ''}</div>
                )}
              </div>
              <div>
                <div className="muted">From WR#</div>
                <div>{moveSrc?.receiptid || ''}</div>
              </div>
            </div>
            <div className="row">
              {moveMode === 'toPicking' ? (
                <div>
                  <div className="muted">To Location</div>
                  <div>{moveDest ? formatLocLabel(moveDest) : ''}</div>
                </div>
              ) : (
                <>
                  <div>
                    <div className="muted">To WR#</div>
                    <div>{moveDest?.receiptid || moveSrc?.receiptid || ''}</div>
                  </div>
                  <div>
                    <div className="muted">To Pallet #</div>
                    <div>{moveDest?.palletnumber || ''}</div>
                  </div>
                </>
              )}
              <div>
                <div className="muted">On Hand</div>
                <div>{Number(moveSrc?.piecesonhand || 0).toLocaleString()}</div>
              </div>
            </div>
            <div className="row">
              {moveMode === 'toPicking' && (
                <div>
                  <label>Storage Type</label>
                  <select value={selectedStorageTypeId} onChange={(e) => setSelectedStorageTypeId(e.target.value)} disabled={storageTypeLocked}>
                    {storageTypes.map(st => (
                      <option key={st.id} value={st.id}>{st.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label>Qty (pieces)</label>
                <input type="number" min={1} value={moveQty} onChange={(e) => setMoveQty(e.target.value)} />
                {moveSrc?.piecesonhand != null && (
                  <div className="muted">On Hand: {Number(moveSrc.piecesonhand).toLocaleString()}</div>
                )}
              </div>
            </div>
            {moveErr && <div className="row" style={{ color: 'tomato' }}>{moveErr}</div>}
            <div className="actions">
              <button className="btn" onClick={() => { setMoveOpen(false); setMoveSrcOptions([]); }}>Cancel</button>
              <button className="btn primary" onClick={doMove}>Move</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
